// Exclusão da conta e dos dados (LGPD): só o dono, confirmando com a senha (ou com o e-mail, para quem entrou com o Google).
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import type { MeAccount } from '@impulso/shared'
import type { DeleteAccountResponse } from '@impulso/shared/api/tenancy'
import { api, errorMessage } from '../../lib/api'
import { useSession } from '../../lib/session'
import { Field } from '../../ui'
import { useConfirm } from '../../ui/confirm'
import { FormError } from '../auth/AuthShell'
import PasswordInput from '../auth/PasswordInput'

export default function DeleteAccountCard({ account }: { account: MeAccount }) {
  const { me, refresh } = useSession()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const [open, setOpen] = useState(false)
  const [secret, setSecret] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const hasPassword = !!me?.user.hasPassword

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const ok = await confirm({
      title: `Excluir ${account.name} para sempre?`,
      message: 'A assinatura é cancelada, as contas de anúncios são desconectadas e todos os dados (campanhas, resultados, páginas e equipe) são apagados. Isso não pode ser desfeito.',
      confirmLabel: 'Excluir tudo',
      danger: true,
    })
    if (!ok) return
    setBusy(true)
    try {
      const r = await api.post<DeleteAccountResponse>(`/accounts/${account.id}/delete`, hasPassword ? { password: secret } : { email: secret })
      if (r.signedOut) {
        window.location.assign('/') // recarrega tudo: nada da conta apagada fica na tela
        return
      }
      await refresh()
      navigate('/app', { replace: true })
    } catch (err) {
      setError(errorMessage(err))
      setBusy(false)
    }
  }

  return (
    <div className="card stack" style={{ gap: 12 }}>
      <div className="card-head">
        <span className="ic red"><Trash2 size={18} /></span>
        <div>
          <h2>Excluir conta e dados</h2>
          <div className="small muted">{account.name}</div>
        </div>
      </div>
      <p className="small muted">Cancela a assinatura, desconecta o Google e a Meta e apaga os dados da conta. Se quiser guardar algo, baixe o relatório antes.</p>
      {!open ? (
        <button type="button" className="btn" style={{ alignSelf: 'flex-start', color: 'var(--red)' }} onClick={() => setOpen(true)}>Quero excluir esta conta</button>
      ) : (
        <form className="stack" style={{ gap: 12 }} onSubmit={submit}>
          <FormError error={error} />
          <Field label={hasPassword ? 'Sua senha, para confirmar' : 'Digite seu e-mail, para confirmar'}>
            {hasPassword
              ? <PasswordInput id={`del-${account.id}`} value={secret} onChange={setSecret} autoComplete="current-password" required />
              : <input className="input" type="email" autoComplete="email" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={me?.user.email} required />}
          </Field>
          <div className="row">
            <button className="btn primary danger" disabled={busy || !secret}>{busy ? 'Excluindo…' : 'Excluir conta'}</button>
            <button type="button" className="btn ghost" disabled={busy} onClick={() => { setOpen(false); setSecret(''); setError(null) }}>Cancelar</button>
          </div>
        </form>
      )}
    </div>
  )
}
